import React from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiPhone, FiMail, FiMapPin, FiClock, 
  FiFacebook, FiInstagram, FiLinkedin, FiYoutube 
} = FiIcons;

// ✅ Exact same logo as Navbar
const TayyabLogo = () => (
  <div className="flex items-center space-x-2.5">
    <div className="relative flex items-center justify-center w-10 h-10">
      <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 5v5M11 5v5" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round"/>
        <rect x="5" y="10" width="8" height="5" rx="1.5" stroke="#38bdf8" strokeWidth="1.8"/>
        <path d="M9 15v3" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round"/>
        <circle cx="9" cy="20" r="1.5" fill="#facc15"/>
        <polyline points="13,21 16,21 18,15 20,27 22,18 24,24 26,21 29,21"
          stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="28" y="9" width="10" height="7" rx="1.5" stroke="#38bdf8" strokeWidth="1.8"/>
        <polygon points="38,10.5 41,8.5 41,17.5 38,15.5" fill="#38bdf8"/>
        <circle cx="32" cy="12.5" r="1.5" fill="#facc15"/>
      </svg>
    </div>
    <div className="flex flex-col leading-none">
      <span className="text-white font-black tracking-[0.12em] text-lg"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
        TAYYAB
      </span>
      <span className="text-blue-400 text-[9px] font-bold tracking-[0.28em] uppercase mt-0.5">
        Electric Studio
      </span>
      <span className="text-gray-500 text-[7px] tracking-[0.15em] mt-0.5">
        POWERING HOMES, SECURING FUTURES.
      </span>
    </div>
  </div>
);

const Footer = () => {
  const quickLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/services', label: 'Services' },
    { path: '/appointments', label: 'Book Appointment' },
    { path: '/contact', label: 'Contact' },
    { path: '/faq', label: 'FAQ' },
    { path: '/lucky-draw', label: 'Lucky Draw 🎰' },
  ];

  const servicesList = [
    'Electrical Installation',
    'CCTV Systems',
    'Smart Home Automation',
    'Solar Power',
    'Security Systems',
    'Annual Maintenance'
  ];

  const contactInfo = [
    { icon: FiPhone, text: '+91 99066 69918', link: 'tel:+919906669918' },
    { icon: FiMail, text: 'tayyabelectricstudio@outlook.com', link: 'mailto:tayyabelectricstudio@outlook.com' },
    { icon: FiMapPin, text: 'Fatehgarh, Baramulla, J&K', link: 'https://maps.google.com' },
    { icon: FiClock, text: '24/7 Emergency Service', link: 'tel:+919906669918' }
  ];

  const socialLinks = [
    { icon: FiFacebook, link: 'https://www.facebook.com/tayyabelectricstudio', label: 'Facebook' },
    { icon: FiInstagram, link: 'https://www.instagram.com/tayyabelectricstudio', label: 'Instagram' },
    { icon: FiLinkedin, link: 'https://www.linkedin.com/company/tayyab-electric-studio-077437392', label: 'LinkedIn' },
    { icon: FiYoutube, link: 'https://www.youtube.com/channel/UCT6vi_i6l9ZYqXB9ih5phwg', label: 'YouTube' }
  ];

  return (
    <footer className="bg-gradient-to-b from-black to-gray-900 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="space-y-6">
            {/* ✅ Logo now matches Navbar exactly */}
            <Link to="/" className="flex items-center">
              <TayyabLogo />
            </Link>
            <p className="text-gray-400 leading-relaxed">
              Powering homes and securing futures with professional electrical and automation
              solutions across Kashmir for over 13 years.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-600 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300"
                  aria-label={social.label}
                >
                  <SafeIcon icon={social.icon} className="text-lg" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-blue-400 transition-colors duration-300 flex items-center space-x-2 group"
                  >
                    <span className="w-1 h-1 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Our Services</h4>
            <ul className="space-y-3">
              {servicesList.map((service, index) => (
                <li key={index}>
                  <Link
                    to="/services"
                    className="text-gray-400 hover:text-blue-400 transition-colors duration-300 flex items-center space-x-2 group"
                  >
                    <span className="w-1 h-1 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <span>{service}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Contact Info</h4>
            <ul className="space-y-4">
              {contactInfo.map((info, index) => (
                <li key={index}>
                  <a
                    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : '_self'}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                    className="text-gray-400 hover:text-blue-400 transition-colors duration-300 flex items-center space-x-3 group"
                  >
                    <SafeIcon icon={info.icon} className="text-lg text-blue-400 group-hover:text-cyan-400 transition-colors duration-300" />
                    <span className="text-sm">{info.text}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-black border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center md:text-left">
          <p className="text-gray-400 text-sm">
            © 2026 Tayyab Electric Studio - A Unit of <b>ARS Trading Corporation</b>. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;